"use client";

import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { setAutoSignOut } from "../services/settingsService";
import { ChevronDown, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const SelectTimeout = ({ timeOut }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { updateSettings, userProfile } = useAuth();
  const { toast } = useToast();

  const timeframes = [
    "Off",
    "10 minutes",
    "30 minutes",
    "1 hour",
    "4 hours",
    "12 hours",
    "24 hours",
  ];

  const [selectedTimeframe, setSelectedTimeframe] = useState(
    timeOut || "4 hours"
  );
  const [customMinutes, setCustomMinutes] = useState("");

  const userId = userProfile?.user_id;

  const selectTime = async (data, closeDialog = true) => {
    setIsLoading(true);
    try {
      const value = data === "Off" ? "off" : data.toLowerCase();
      const apiCall = await setAutoSignOut(userId, value);
      if (apiCall.status === 200 && apiCall.data.status === true) {
        const newSettings = apiCall.data.data;
        setSelectedTimeframe(data);
        updateSettings(newSettings);
        if (closeDialog) {
          setIsOpen(false);
        }
        toast({
          description: "Timeout Successfully Updated",
          variant: "success",
        });
      }
    } catch (err) {
      toast({
        description: "Error Updating Timeframe",
        variant: "default",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomChange = (e) => {
    const value = e.target.value;
    setCustomMinutes(value);
  };

  const handleCustomSubmit = () => {
    const minutes = parseInt(customMinutes, 10);
    if (minutes >= 10 && minutes <= 1440) {
      selectTime(`${minutes} minutes`);
    } else if (customMinutes) {
      toast({
        description: "Please enter a value between 10 and 1440 minutes",
        variant: "default",
      });
    }
  };

  const handlePresetSelect = (timeframe) => {
    if (isLoading) return;
    setCustomMinutes("");
    selectTime(timeframe);
  };

  const getDisplayValue = () => {
    if (selectedTimeframe === "off") return "Off";
    return selectedTimeframe;
  };

  const isSelected = (timeframe) => {
    // If custom input has value, no preset should be selected
    if (customMinutes) return false;
    if (selectedTimeframe === "off" && timeframe === "Off") return true;
    return selectedTimeframe === timeframe;
  };

  useEffect(() => {
    if (timeOut && timeOut.length > 0) {
      setSelectedTimeframe(timeOut === "off" ? "Off" : timeOut);
    }
  }, [timeOut]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="relative w-30 h-10 rounded-full bg-transparent border border-gray-100 border-opacity-35 text-text-secondary flex justify-between px-4"
        >
          {isLoading ? (
            <Loader2 className="animate-spin text-gray-700" />
          ) : (
            <span className="text-sm font-semibold">{getDisplayValue()}</span>
          )}
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md p-6 rounded-lg shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-700 mt-5">
            Set auto sign-out time
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Specify how long your session can remain inactive before you're
            automatically signed out for security purposes.
          </p>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {timeframes.map((timeframe) => (
            <div
              key={timeframe}
              onClick={() => handlePresetSelect(timeframe)}
              className={`flex items-center justify-between px-4 py-3 cursor-pointer border rounded-md ${
                isSelected(timeframe) ? "border-rose-500" : "border-gray-200"
              } hover:bg-gray-100 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span className="text-sm font-medium text-gray-800">
                {timeframe}
              </span>
            </div>
          ))}

          {/* Custom Minutes Input */}
          <div
            className={`flex items-center gap-2 px-4 py-3 border rounded-md ${customMinutes ? "border-rose-500" : "border-gray-200"}`}
          >
            <input
              type="number"
              min="10"
              max="1440"
              value={customMinutes}
              onChange={handleCustomChange}
              placeholder="Custom (10-1440)"
              className="flex-1 text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-sm text-gray-500">min</span>
            <Button
              size="sm"
              onClick={handleCustomSubmit}
              disabled={isLoading || !customMinutes}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-1 h-7"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectTimeout;
